// Imperative mail-context fold — the mail twin of chatFold.js.
//
// Mail (@symbo-ls/workspace-mail, the built-in email client —
// architecture/MAIL.md) is a lazy sub-app: only a slim eager slice rides
// sharedLibraries.js, and the FULL context (components + functions) folds
// HERE, on demand, through the same add-missing mechanism the lazy route
// folds use (foldSharedLibContext — shared/pages/lazyPage.js). The docked
// MailOverlay mounts behind the shell-root MailOverlaySlot (workspace/app.js)
// that this fold fills once its definition is registered.
//
// Every opener funnels through ensureMailContext (or ensures first): the
// header envelope (shared/components/AppHeader.js), the mail package's own
// openMailDrawer / toggleMailDrawer, and future deep-links / toast actions.
import _wsShared from '@symbo-ls/workspace-shared'
const shellFeatureEnabled = (...args) => _wsShared.functions.featureEnabled(...args)
const shellFoldSharedLibContext = (...args) => _wsShared.pages.foldSharedLibContext(...args)
let _mailFoldInFlight = null

// Fill the shell-root MailOverlaySlot with the real overlay AFTER the fold
// registered its definition. DOMQL resolves bare-key extends at parent parse
// time, so app.js cannot mount `MailOverlay: {}` directly — the slot +
// post-fold `set` is the same deferred-resolution mechanism lazyPage uses for
// route bodies. Idempotent via the slot's own marker.
const _mountMailOverlay = (rootEl) => {
  const slot = rootEl?.MailOverlaySlot
  if (!slot || typeof slot.set !== 'function' || slot.__mailOverlayMounted) return
  slot.__mailOverlayMounted = true
  slot.set({
    extends: 'MailOverlay',
    // Restates the base component's `_mailDrawerOpen` gate + the /flags
    // feature gate — a mount-level `hide` REPLACES the base hide (verified
    // live on the Messenger slot, 2026-07-25).
    hide: (el, s) => !s.root?._mailDrawerOpen || !shellFeatureEnabled(s.root, 'mail')
  })
}

// Fold the full mail context into the live registry (idempotent,
// single-flight, retryable on failure). Returns true when the context is
// registered, false when the fold failed (callers should bail quietly —
// a header click on a broken chunk must not throw).
export const ensureMailContext = async function () {
  // TWO roots, deliberately distinct: foldSharedLibContext registers into the
  // ELEMENT root's `.context` (same contract as the lazyPage route folds,
  // which pass `el.getRoot()`), while the latch lives on the STATE root.
  // Passing the state root to the fold silently registers nothing — its
  // `.context` is undefined, so both add-missing loops skip (caught live on
  // the chat fold: foldOk=true, zero components registered, the overlay
  // mounting as an empty unknown-key div).
  const rootEl = typeof this?.getRoot === 'function' ? this.getRoot() : this
  const stateRoot = this?.state?.root || rootEl?.state?.root || rootEl?.state || null
  if (!rootEl || !stateRoot) return false
  if (stateRoot._mailCtxFolded) return true
  if (!_mailFoldInFlight) {
    _mailFoldInFlight = import('@symbo-ls/workspace-mail/context.js')
      .then((mod) => {
        const lib = mod?.default || mod
        if (!lib || (!lib.components && !lib.functions)) {
          throw new Error('mail context resolved empty — stale/mismatched bundle')
        }
        shellFoldSharedLibContext(rootEl, lib)
        _mountMailOverlay(rootEl)
        // Latch flips ONCE per app lifetime — the registry keeps the folded
        // components across tenant switches, so this key is deliberately NOT
        // in the workspace-scoped wipe registry (mirrors _chatCtxFolded).
        stateRoot.update({ _mailCtxFolded: true }, { preventFetch: true })
        return true
      })
      .catch((err) => {
        _mailFoldInFlight = null
        console.error('[mail] context fold failed:', err?.message || err)
        return false
      })
  }
  return _mailFoldInFlight
}
